import { memo, useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

/**
 * LowerThird — the TV caption plate for the Ringmaster's narration, designed to
 * drop into ProsceniumFrame's `lowerThird` slot.
 *
 * Renders the current caption in Grenze at the --stage-text-caption scale on a
 * translucent ink plate with a gold top rule. A new caption slides up (AnimatePresence
 * keyed on the caption text). The plate auto-hides 2s after `isSpeaking` goes
 * false, so it lingers briefly on the last line then clears — the scene isn't left
 * with a stale caption.
 *
 * Props are primitives (caption, isSpeaking) so a zustand caller can select them
 * individually.
 */

const HIDE_DELAY_MS = 2000;

export interface LowerThirdProps {
  caption: string | null;
  isSpeaking: boolean;
}

function LowerThirdImpl({ caption, isSpeaking }: LowerThirdProps) {
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(isSpeaking && !!caption);

  useEffect(() => {
    if (isSpeaking && caption) {
      setVisible(true);
      return;
    }
    // Speaking stopped (or no caption) — linger, then hide.
    if (!caption) {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(false), HIDE_DELAY_MS);
    return () => clearTimeout(t);
  }, [isSpeaking, caption]);

  return (
    <AnimatePresence mode="wait">
      {visible && caption && (
        <motion.div
          key={caption}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: 12 }}
          transition={{ duration: reduced ? 0 : 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            background: 'rgba(11, 7, 9, 0.82)', // --ink-1 translucent
            borderTop: '2px solid var(--gold-4, #c9923f)',
            borderRadius: 'var(--radius-md, 12px)',
            padding: '20px 40px',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.6)',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-display, serif)',
              fontSize: 'var(--stage-text-caption, 44px)',
              lineHeight: 1.25,
              color: 'var(--gold-6, #f7e3b0)',
              letterSpacing: '0.02em',
            }}
          >
            {caption}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const LowerThird = memo(LowerThirdImpl);
